import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Phone, Mail } from "lucide-react";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", relationship: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/api/contacts");
    setContacts(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/contacts", form);
      toast.success("Contact added!");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", relationship: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/api/contacts/${id}`); load(); };

  const colors = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1"];

  return (
    <div data-testid="contacts-page">
      <PageHeader
        title="Contacts"
        subtitle="Important family contacts."
        color="#B8CFA9"
        action={<NBButton testId="add-contact-btn" color="#B8CFA9" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Contact</NBButton>}
      />

      {contacts.length === 0 ? (
        <EmptyState title="No contacts yet" subtitle="Add doctors, teachers, and other family contacts." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((c, i) => (
            <NBCard key={c.id} color={colors[i % colors.length]} data-testid={`contact-${c.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-black" style={{ fontFamily: "var(--hh-display)" }}>{c.name}</h4>
                  {c.relationship && <span className="nb-badge bg-white mt-1 inline-block">{c.relationship}</span>}
                </div>
                <button onClick={() => remove(c.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-contact-${c.id}`}>
                  <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                </button>
              </div>
              {c.phone && <p className="text-sm mt-3 flex items-center gap-2"><Phone strokeWidth={2.5} className="w-3.5 h-3.5" /> <a href={`tel:${c.phone}`} className="font-bold underline">{c.phone}</a></p>}
              {c.email && <p className="text-sm mt-1 flex items-center gap-2 truncate"><Mail strokeWidth={2.5} className="w-3.5 h-3.5" /> <a href={`mailto:${c.email}`} className="font-bold underline truncate">{c.email}</a></p>}
              {c.notes && <p className="text-sm mt-2 italic">{c.notes}</p>}
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Contact</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="contact-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <NBInput testId="contact-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <NBInput testId="contact-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <NBInput testId="contact-relationship" placeholder="Relationship (e.g. Doctor, Teacher)" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} />
              <NBTextarea testId="contact-notes" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <NBButton testId="submit-contact" type="submit" color="#B8CFA9" className="w-full">Save Contact</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
